import {
  Directive,
  ElementRef,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  Renderer2,
  RendererStyleFlags2,
} from '@angular/core';
import { fromEvent, merge, Subscription } from 'rxjs';
import { filter, throttleTime } from 'rxjs/operators';

@Directive({
  selector: '.floating-paginator',
})
export class FloatingPaginatorFadeDirective implements OnInit, OnDestroy {
  @Input() floatingControlsAutoHide = false;
  @Input() floatingControlsFixedAtScrollEnd = false;

  // Posição padrão (main viewport)
  private readonly MAIN_FLOAT_BOTTOM = 30;
  private readonly MAIN_HIDE_BOTTOM = -60;

  // Posição dentro de modal (side-by-side com floating-bar)
  private readonly MODAL_FLOAT_BOTTOM = 65;

  // Posição empilhada (modal sm ou mobile/tablet com floating-bar)
  private readonly MODAL_STACKED_FLOAT_BOTTOM = 129;

  private readonly HIDE_DELAY_MS = 2000;
  private readonly TOUCH_SCROLL_THRESHOLD = 8;
  private readonly TOUCH_CLICK_SUPPRESSION_MS = 500;

  private hideTimer?: ReturnType<typeof setTimeout>;
  private sub!: Subscription;
  private touchScrollSub = new Subscription();
  private modalContentObserver?: MutationObserver;
  private modalResizeObserver?: ResizeObserver;
  private modalWidth = -1;

  // Contexto de modal
  private isInsideModal = false;
  private dialogSurface: HTMLElement | null = null;
  private modalScrollContainer: HTMLElement | null = null;
  private floatingBar: HTMLElement | null = null;
  private floatingBarParent: Node | null = null;
  private floatingBarNextSibling: Node | null = null;
  private floatingControlsViewport: HTMLElement | null = null;
  private hasFloatingBar = false;
  private isModalStacked = false;

  // Valores dinâmicos calculados por contexto
  private floatBottom = this.MAIN_FLOAT_BOTTOM;
  private hideBottom = this.MAIN_HIDE_BOTTOM;

  constructor(
    private el: ElementRef<HTMLElement>,
    private rd: Renderer2,
    private zone: NgZone
  ) {}

  ngOnInit(): void {
    this.dialogSurface = this.el.nativeElement.closest('.mat-mdc-dialog-surface');
    this.modalScrollContainer = this.el.nativeElement.closest('.mat-mdc-dialog-content');
    this.isInsideModal = !!this.dialogSurface;

    if (this.isInsideModal) {
      setTimeout(() => this.setupModalMode());
    } else {
      this.setupMainMode();
    }
  }

  ngOnDestroy(): void {
    this.clearHideTimer();
    this.sub?.unsubscribe();
    this.touchScrollSub.unsubscribe();
    this.modalContentObserver?.disconnect();
    this.modalResizeObserver?.disconnect();

    if (this.isInsideModal && this.dialogSurface) {
      this.restoreFloatingBar();
      if (
        this.floatingControlsViewport &&
        this.rd.parentNode(this.floatingControlsViewport) === this.dialogSurface
      ) {
        this.rd.removeChild(this.dialogSurface, this.floatingControlsViewport);
      }
      this.rd.removeClass(this.dialogSurface, 'has-floating-paginator');
      this.rd.removeClass(this.dialogSurface, 'has-floating-bar');
      this.rd.removeClass(this.dialogSurface, 'modal-stacked');
      this.rd.removeClass(this.dialogSurface, 'modal-show-nav');
      this.rd.removeClass(this.dialogSurface, 'modal-show-labels');
      this.rd.removeClass(this.dialogSurface, 'floating-controls-auto-hide');
      this.rd.removeClass(this.dialogSurface, 'floating-controls-hidden');
      this.rd.removeClass(this.dialogSurface, 'floating-controls-at-scroll-end');
    }
  }

  /* -------- Modos de operação -------- */

  private setupMainMode(): void {
    this.floatBottom = this.MAIN_FLOAT_BOTTOM;
    this.hideBottom = this.MAIN_HIDE_BOTTOM;

    this.rd.setStyle(this.el.nativeElement, 'bottom', `${this.floatBottom}px`);
    this.rd.setStyle(this.el.nativeElement, 'transition', 'bottom 0.5s ease-in-out');

    this.zone.runOutsideAngular(() => {
      const main = document.querySelector('main') as HTMLElement | null;

      if (main) {
        this.setupTouchScrollBridge(this.el.nativeElement, main);
      }

      const scroll$ = fromEvent(main ?? window, 'scroll', { passive: true });
      const move$ = fromEvent<MouseEvent>(window, 'mousemove').pipe(
        throttleTime(100),
        filter(evt => !this.el.nativeElement.contains(evt.target as Node))
      );
      const enter$ = fromEvent(this.el.nativeElement, 'mouseenter');
      const leave$ = fromEvent(this.el.nativeElement, 'mouseleave');

      this.sub = merge(scroll$, move$, enter$, leave$).subscribe((evt: Event) => {
        switch (evt.type) {
          case 'mouseenter':
            this.show();
            this.clearHideTimer();
            break;
          default:
            this.show();
            this.scheduleHide();
        }
      });

      this.scheduleHide();
    });
  }

  private setupModalMode(): void {
    this.floatingBar = this.dialogSurface!.querySelector('.floating-bar');
    this.hasFloatingBar = !!this.floatingBar;

    // Move o elemento para fora do scroll container
    this.rd.appendChild(this.dialogSurface, this.el.nativeElement);

    // Classes para CSS reagir
    this.rd.addClass(this.dialogSurface, 'has-floating-paginator');
    if (this.hasFloatingBar) {
      this.rd.addClass(this.dialogSurface, 'has-floating-bar');
    }
    this.updateModalLayout();

    // Posiciona o paginator — sempre visível no modal
    this.rd.setStyle(this.el.nativeElement, 'position', 'absolute');
    this.rd.setStyle(this.el.nativeElement, 'bottom', `${this.floatBottom}px`);

    const scrollContainer = this.modalScrollContainer;
    if (scrollContainer) {
      this.zone.runOutsideAngular(() => {
        this.setupTouchScrollBridge(this.el.nativeElement, scrollContainer);
        if (this.floatingBar) {
          this.setupTouchScrollBridge(this.floatingBar, scrollContainer);
        }
      });
    }

    if (this.floatingControlsAutoHide) {
      this.setupModalAutoHide();
    }
    this.zone.runOutsideAngular(() => {
      this.modalResizeObserver = new ResizeObserver(() => this.updateModalLayout());
      this.modalResizeObserver.observe(this.dialogSurface!);
    });
  }

  private updateModalLayout(): void {
    if (!this.dialogSurface || this.modalWidth === this.dialogSurface.clientWidth) return;
    this.modalWidth = this.dialogSurface.clientWidth;
    this.isModalStacked = this.hasFloatingBar && this.isNarrowModal();
    this.floatBottom = this.isModalStacked ? this.MODAL_STACKED_FLOAT_BOTTOM : this.MODAL_FLOAT_BOTTOM;
    for (const [name, active] of [
      ['modal-stacked', this.isModalStacked],
      ['modal-show-nav', this.modalWidth >= 900],
      ['modal-show-labels', this.modalWidth >= 1100],
    ] as const) {
      if (active) this.rd.addClass(this.dialogSurface, name);
      else this.rd.removeClass(this.dialogSurface, name);
    }
    this.rd.setStyle(this.el.nativeElement, 'bottom', `${this.floatBottom}px`);
    if (this.floatingControlsViewport) {
      const footerHeight = this.dialogSurface.querySelector<HTMLElement>('.modal-footer')?.offsetHeight ?? 0;
      const footerGap = Math.max(0, this.MODAL_FLOAT_BOTTOM - footerHeight);
      this.rd.setStyle(this.floatingControlsViewport, 'bottom', `${footerHeight}px`);
      this.rd.setStyle(this.floatingControlsViewport, 'height', `${(this.isModalStacked ? 120 : 56) + footerGap}px`);
      this.rd.setStyle(this.floatingControlsViewport, '--floating-controls-footer-gap', `${footerGap}px`, RendererStyleFlags2.DashCase);
    }
  }

  private setupModalAutoHide(): void {
    const scrollContainer = this.modalScrollContainer;
    if (!scrollContainer) return;

    this.createFloatingControlsViewport();
    this.rd.addClass(this.dialogSurface, 'floating-controls-auto-hide');

    this.zone.runOutsideAngular(() => {
      const scroll$ = fromEvent(scrollContainer, 'scroll', { passive: true });
      const move$ = fromEvent<MouseEvent>(this.dialogSurface!, 'mousemove').pipe(
        throttleTime(100),
        filter(evt => !this.isFloatingControl(evt.target as Node))
      );
      const enterPaginator$ = fromEvent(this.el.nativeElement, 'mouseenter');
      const leavePaginator$ = fromEvent(this.el.nativeElement, 'mouseleave');
      const floatingBarEvents$ = this.floatingBar
        ? merge(fromEvent(this.floatingBar, 'mouseenter'), fromEvent(this.floatingBar, 'mouseleave'))
        : [];

      this.sub = merge(scroll$, move$, enterPaginator$, leavePaginator$, floatingBarEvents$).subscribe((evt: Event) => {
        if (this.shouldKeepVisibleAtScrollEnd()) {
          this.show();
          this.clearHideTimer();
          return;
        }

        if (evt.type === 'mouseenter') {
          this.show();
          this.clearHideTimer();
          return;
        }

        this.show();
        this.scheduleHide();
      });

      this.modalContentObserver = new MutationObserver(() => this.updateModalControlsAfterContentChange());
      this.modalContentObserver.observe(scrollContainer, { childList: true, subtree: true });
      this.scheduleHide();
    });
  }

  /* -------- Helpers -------- */

  private createFloatingControlsViewport(): void {
    const viewport = this.rd.createElement('div');
    const footerHeight = this.dialogSurface?.querySelector<HTMLElement>('.modal-footer')?.offsetHeight ?? 0;
    const footerGap = Math.max(0, this.MODAL_FLOAT_BOTTOM - footerHeight);
    const controlsHeight = this.isModalStacked ? 120 : 56;

    this.rd.addClass(viewport, 'floating-controls-viewport');
    this.rd.setStyle(viewport, 'bottom', `${footerHeight}px`);
    this.rd.setStyle(viewport, 'height', `${controlsHeight + footerGap}px`);
    this.rd.setStyle(viewport, '--floating-controls-footer-gap', `${footerGap}px`, RendererStyleFlags2.DashCase);
    this.rd.appendChild(this.dialogSurface, viewport);
    this.rd.appendChild(viewport, this.el.nativeElement);
    this.floatingControlsViewport = viewport;

    if (!this.floatingBar) return;

    this.floatingBarParent = this.rd.parentNode(this.floatingBar);
    this.floatingBarNextSibling = this.rd.nextSibling(this.floatingBar);
    this.rd.appendChild(viewport, this.floatingBar);
  }

  private restoreFloatingBar(): void {
    if (!this.floatingBar || !this.floatingBarParent || !this.floatingControlsViewport?.contains(this.floatingBar)) {
      return;
    }

    if (this.floatingBarNextSibling && this.rd.parentNode(this.floatingBarNextSibling) === this.floatingBarParent) {
      this.rd.insertBefore(this.floatingBarParent, this.floatingBar, this.floatingBarNextSibling);
      return;
    }

    this.rd.appendChild(this.floatingBarParent, this.floatingBar);
  }

  private setupTouchScrollBridge(control: HTMLElement, scrollContainer: HTMLElement): void {
    let touchIdentifier: number | null = null;
    let touchStartX = 0;
    let touchStartY = 0;
    let previousTouchY = 0;
    let isDragging = false;
    let suppressClickUntil = 0;

    const resetGesture = (): void => {
      touchIdentifier = null;
      isDragging = false;
    };

    this.touchScrollSub.add(
      fromEvent<TouchEvent>(control, 'touchstart', { passive: true }).subscribe(event => {
        if (event.touches.length !== 1) {
          resetGesture();
          return;
        }

        const touch = event.touches[0];
        touchIdentifier = touch.identifier;
        touchStartX = touch.clientX;
        touchStartY = touch.clientY;
        previousTouchY = touch.clientY;
        isDragging = false;
      })
    );

    this.touchScrollSub.add(
      fromEvent<TouchEvent>(control, 'touchmove', { passive: false }).subscribe(event => {
        const touch = Array.from(event.touches).find(item => item.identifier === touchIdentifier);
        if (!touch) return;

        if (!isDragging) {
          const distanceX = Math.abs(touch.clientX - touchStartX);
          const distanceY = Math.abs(touch.clientY - touchStartY);
          if (distanceX < this.TOUCH_SCROLL_THRESHOLD && distanceY < this.TOUCH_SCROLL_THRESHOLD) return;
          if (distanceX >= distanceY) {
            resetGesture();
            return;
          }
          isDragging = true;
        }

        if (event.cancelable) event.preventDefault();
        scrollContainer.scrollTop += previousTouchY - touch.clientY;
        previousTouchY = touch.clientY;
        suppressClickUntil = performance.now() + this.TOUCH_CLICK_SUPPRESSION_MS;
      })
    );

    this.touchScrollSub.add(fromEvent<TouchEvent>(control, 'touchend').subscribe(() => resetGesture()));
    this.touchScrollSub.add(fromEvent<TouchEvent>(control, 'touchcancel').subscribe(() => resetGesture()));
    this.touchScrollSub.add(
      fromEvent<MouseEvent>(control, 'click', { capture: true }).subscribe(event => {
        if (performance.now() > suppressClickUntil) return;

        suppressClickUntil = 0;
        event.preventDefault();
        event.stopImmediatePropagation();
      })
    );
  }

  private show(): void {
    if (this.isInsideModal && this.floatingControlsAutoHide) {
      this.rd.removeClass(this.dialogSurface, 'floating-controls-hidden');
      return;
    }
    this.rd.setStyle(this.el.nativeElement, 'bottom', `${this.floatBottom}px`);
  }

  private hide(): void {
    if (this.isInsideModal && this.floatingControlsAutoHide) {
      if (!this.isFloatingControlHovered() && !this.shouldKeepVisibleAtScrollEnd()) {
        this.rd.addClass(this.dialogSurface, 'floating-controls-hidden');
      }
      return;
    }
    if (!this.el.nativeElement.matches(':hover')) {
      this.rd.setStyle(this.el.nativeElement, 'bottom', `${this.hideBottom}px`);
    }
  }

  private scheduleHide(): void {
    this.clearHideTimer();
    this.hideTimer = setTimeout(() => this.hide(), this.HIDE_DELAY_MS);
  }

  private clearHideTimer(): void {
    if (this.hideTimer) {
      clearTimeout(this.hideTimer);
      this.hideTimer = undefined;
    }
  }

  private updateModalControlsAfterContentChange(): void {
    if (this.shouldKeepVisibleAtScrollEnd()) {
      this.show();
      this.clearHideTimer();
      return;
    }
    this.scheduleHide();
  }

  private shouldKeepVisibleAtScrollEnd(): boolean {
    if (!this.floatingControlsFixedAtScrollEnd || !this.modalScrollContainer) {
      this.rd.removeClass(this.dialogSurface, 'floating-controls-at-scroll-end');
      return false;
    }

    const { clientHeight, scrollHeight, scrollTop } = this.modalScrollContainer;
    const isAtScrollEnd = scrollTop + clientHeight >= scrollHeight - 1;
    if (isAtScrollEnd) {
      this.rd.addClass(this.dialogSurface, 'floating-controls-at-scroll-end');
    } else {
      this.rd.removeClass(this.dialogSurface, 'floating-controls-at-scroll-end');
    }
    return isAtScrollEnd;
  }

  private isFloatingControl(target: Node): boolean {
    return this.el.nativeElement.contains(target) || !!this.floatingBar?.contains(target);
  }

  private isFloatingControlHovered(): boolean {
    return this.el.nativeElement.matches(':hover') || !!this.floatingBar?.matches(':hover');
  }

  /**
   * Verifica se o modal é estreito (< 700px de largura real).
   */
  private isNarrowModal(): boolean {
    const overlayPane = this.dialogSurface?.closest('.cdk-overlay-pane');
    const isSmModal = overlayPane?.classList.contains('modal-size-sm') ?? false;
    const modalWidth = this.dialogSurface?.clientWidth ?? 0;
    return isSmModal || modalWidth < 700;
  }
}
